import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  CompanyShareableType,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Company } from '~hooks/useCompany/models';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { BasicEquipment, Equipment } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { EquipmentDialog } from '~pages/Settings/Administration/EquipmentManagement/components/EquipmentDialog';
import {
  EquipmentFormHandlers,
  EquipmentFormValues,
} from '~pages/Settings/Administration/EquipmentManagement/EquipmentForm';
import { ExternalEquipmentFormHandlers } from '~pages/Settings/Administration/EquipmentManagement/ExternalEquipmentForm';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface ManagedVendorEquipmentTabProps {
  managedCompanyId: string;
  managedCompany: Company;
}

export const ManagedVendorEquipmentTab = observer(
  ({ managedCompanyId, managedCompany }: ManagedVendorEquipmentTabProps) => {
    const {
      getEquipmentByCompanyId,
      createEquipmentByCompanyId,
      updateEquipment,
      deleteEquipment,
      getEquipmentById,
    } = useEquipment();
    const [isEquipmentUpdating, setIsEquipmentUpdating] = useState(false);
    const {
      updateCompanyShare,
      createCompanyShareByCompanyId,
      deleteCompanyShare,
      getCompanySharesByCompanyId,
      isUpdating: isUpdatingCompanyShares,
    } = useCompanyShares();
    const { toasterStore, userStore } = useStores();
    const [searchQuery, setSearchQuery] = useState('');
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const [isEquipmentDialogOpen, setIsEquipmentDialogOpen] = useState(false);
    const [isExternalEquipmentDialogOpen, setIsExternalEquipmentDialogOpen] =
      useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState<
      Equipment | BasicEquipment | null
    >(null);
    const [companyShares, setCompanyShares] = useState<CompanyShare[]>([]);
    const [deleteEquipmentDialogState, setDeleteEquipmentDialogState] = useState<{
      isDeleting?: boolean;
      isOpen: boolean;
      equipmentId?: string;
    }>({ isOpen: false });
    const equipmentFormRef = useRef<EquipmentFormHandlers>(null);
    const externalEquipmentFormRef = useRef<ExternalEquipmentFormHandlers>(null);

    const companyId = getEffectiveUserCompanyId(userStore);

    const {
      goToPage: goToEquipmentPage,
      isFetching: isFetchingEquipment,
      pagination: equipmentPagination,
      reloadCurrentPage: reloadEquipmentPage,
      rows: equipment,
      startOver: startEquipmentOver,
    } = useCursorPagination<Equipment>({
      fetchPage: ({ cursor, limit, link, signal }) =>
        getEquipmentByCompanyId({
          companyId,
          cursor,
          limit,
          link,
          managedCompanyId,
          query: searchQuery,
          signal,
        }),
      limit: MAX_API_PAGE_SIZE,
    });

    useEffect(() => {
      if (managedCompanyId) {
        void startEquipmentOver();
      }
    }, [companyId, managedCompanyId, searchQuery, startEquipmentOver]);

    const handleCloseEquipmentDialog = () => {
      setIsEquipmentDialogOpen(false);
      setSelectedEquipment(null);
      setCompanyShares([]);
      setAsyncErrors([]);
    };

    const handleCloseExternalEquipmentDialog = () => {
      setIsExternalEquipmentDialogOpen(false);
      setSelectedEquipment(null);
      setCompanyShares([]);
      setAsyncErrors([]);
    };

    const handleOpenEquipmentDialog = () => {
      setAsyncErrors([]);
      setSelectedEquipment(null);
      setIsEquipmentDialogOpen(true);
    };

    const handleEditEquipment = async (id: string) => {
      try {
        const equipment = await getEquipmentById(id, companyId, managedCompanyId);
        setSelectedEquipment(equipment as Equipment);

        if (equipment) {
          await getCompanySharesByCompanyId({
            companyId,
            managedCompanyId,
            company_shareable_type: CompanyShareableType.EQUIPMENT,
            company_shareable_id: id,
            sender_company_id: managedCompanyId,
            callBack: setCompanyShares,
          });
        }

        const isExternal = !!equipment.companyShare?.id;
        if (isExternal) {
          setIsExternalEquipmentDialogOpen(true);
        } else {
          setIsEquipmentDialogOpen(true);
        }
      } catch {
        // Error is already handled in the hook
      }
    };

    const handleCompanySharesUpdate = async (
      data: Pick<EquipmentFormValues, 'companyShares'>,
    ) => {
      if (!selectedEquipment) return;

      const companySharesPresentOnSubmit = data.companyShares?.filter(
        (submittedCompanyShare) => !!submittedCompanyShare.receiverCompany.id,
      );

      // determine creates, updates, deletes
      const companySharesToCreate = companySharesPresentOnSubmit?.filter(
        (submittedCompanyShare) =>
          !companyShares.some(
            (existingCompanyShare) =>
              existingCompanyShare.receiverCompany.id ===
              submittedCompanyShare.receiverCompany.id,
          ),
      );

      const companySharesToUpdate = companySharesPresentOnSubmit?.filter(
        (submittedCompanyShare) => submittedCompanyShare.id,
      );

      const companySharesToDelete = companyShares.filter(
        (existingCompanyShare) =>
          companySharesPresentOnSubmit &&
          !companySharesPresentOnSubmit.some(
            (submittedCompanyShare) =>
              existingCompanyShare.receiverCompany.id ===
              submittedCompanyShare.receiverCompany.id,
          ),
      );

      if (companySharesToCreate?.length) {
        await Promise.all(
          companySharesToCreate.map((companyShare) =>
            createCompanyShareByCompanyId({
              companyId,
              managedCompanyId,
              company_shareable_id: selectedEquipment.id,
              receiver_company_id: companyShare.receiverCompany.id as string,
              company_shareable_type: 'Equipment' as CompanyShareableType,
              dispatchable: companyShare.dispatchable?.id === 'dispatchable',
              ticket_match_id: companyShare.ticketMatchId || undefined,
            }),
          ),
        );
      }

      if (companySharesToUpdate?.length) {
        await Promise.all(
          companySharesToUpdate.map((companyShare) =>
            updateCompanyShare(
              {
                companyShareId: companyShare.id as string,
                dispatchable: companyShare.dispatchable?.id === 'dispatchable',
                ticket_match_id: companyShare.ticketMatchId || undefined,
              },
              companyId,
              managedCompanyId,
            ),
          ),
        );
      }

      if (companySharesToDelete?.length) {
        await Promise.all(
          companySharesToDelete.map((companyShare) =>
            deleteCompanyShare(companyShare.id as string, companyId, managedCompanyId),
          ),
        );
      }
    };

    const handleSubmitEquipment = () => {
      if (!equipmentFormRef?.current) return;

      equipmentFormRef.current.submit(async (data: EquipmentFormValues) => {
        setAsyncErrors([]);
        setIsEquipmentUpdating(true);

        try {
          if (selectedEquipment) {
            await updateEquipment({
              equipment: { ...data, id: selectedEquipment.id } as Equipment,
              companyId,
              managedCompanyId,
            });

            await handleCompanySharesUpdate(data);

            const updatedEquipment = await getEquipmentById(
              selectedEquipment.id,
              companyId,
              managedCompanyId,
            );

            if (!toasterStore.alerts.length) {
              toasterStore.push(
                alert(
                  t('administration.equipment.equipment_updated', {
                    name: updatedEquipment.name,
                    equipment_id: updatedEquipment.equipmentId || '',
                  }),
                  AlertTypes.success,
                ),
              );
            }
          } else {
            const createdEquipment = await createEquipmentByCompanyId({
              equipment: data,
              companyId,
              managedCompanyId,
            });

            if (data.companyShares?.length && createdEquipment) {
              await Promise.all(
                data.companyShares
                  .filter((cs) => !!cs.receiverCompany.id)
                  .map((companyShare) =>
                    createCompanyShareByCompanyId({
                      companyId,
                      managedCompanyId,
                      company_shareable_id: createdEquipment.id,
                      receiver_company_id: companyShare.receiverCompany.id as string,
                      company_shareable_type: 'Equipment' as CompanyShareableType,
                      dispatchable: companyShare.dispatchable?.id === 'dispatchable',
                      ticket_match_id: companyShare.ticketMatchId || undefined,
                    }),
                  ),
              );
            }

            if (!toasterStore.alerts.length && createdEquipment) {
              toasterStore.push(
                alert(
                  t('administration.equipment.equipment_created', {
                    name: createdEquipment.name,
                    equipment_id: createdEquipment.equipmentId || '',
                  }),
                  AlertTypes.success,
                ),
              );
            }
          }

          const isEditingEquipment = Boolean(selectedEquipment);
          handleCloseEquipmentDialog();
          // a new record has no known page, so it returns to page 1; an edit keeps the current page
          if (isEditingEquipment) {
            void reloadEquipmentPage();
          } else {
            void goToEquipmentPage();
          }
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 422) {
            const e = error as AxiosError<ModelError_Response>;
            setAsyncErrors(e.response?.data.error.errors || []);
          } else {
            connection.handleRequestError(
              error,
              t('error_messages.equipment.failed_to_save'),
            );
          }
        } finally {
          setIsEquipmentUpdating(false);
        }
      });
    };

    const handleSubmitExternalEquipment = () => {
      if (!externalEquipmentFormRef?.current) return;

      externalEquipmentFormRef.current.submit(async (data, dirtyFields) => {
        setAsyncErrors([]);
        setIsEquipmentUpdating(true);

        try {
          if (selectedEquipment && selectedEquipment.companyShare?.id) {
            if (dirtyFields?.externalId || dirtyFields?.ticketMatchId) {
              await updateCompanyShare(
                {
                  companyShareId: selectedEquipment.companyShare.id,
                  ...(dirtyFields.externalId
                    ? { external_id: data.externalId || '' }
                    : {}),
                  ...(dirtyFields.ticketMatchId
                    ? { ticket_match_id: data.ticketMatchId || '' }
                    : {}),
                },
                companyId,
                managedCompanyId,
              );
            }

            await handleCompanySharesUpdate(data);

            // re-fetch equipment to get latest data
            const updatedEquipment = await getEquipmentById(
              selectedEquipment.id,
              companyId,
              managedCompanyId,
            );

            if (!toasterStore.alerts.length) {
              toasterStore.push(
                alert(
                  t('administration.equipment.equipment_updated', {
                    name: updatedEquipment.name,
                    equipment_id: updatedEquipment.equipmentId || '',
                  }),
                  AlertTypes.success,
                ),
              );
            }
          }

          handleCloseExternalEquipmentDialog();
          void reloadEquipmentPage();
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 422) {
            const e = error as AxiosError<ModelError_Response>;
            setAsyncErrors(e.response?.data.error.errors || []);
          } else {
            connection.handleRequestError(
              error,
              t('error_messages.equipment.failed_to_save'),
            );
          }
        } finally {
          setIsEquipmentUpdating(false);
        }
      });
    };

    const handleDeleteCompanyShare = async (companyShareId: string) => {
      try {
        await deleteCompanyShare(companyShareId, companyId, managedCompanyId);
        toasterStore.push(
          alert(t('administration.equipment.share_removed'), AlertTypes.success),
        );
        void reloadEquipmentPage();
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.company_shares.failed_to_unshare'),
        );
      }
    };

    const deleteEquipmentCallback = async () => {
      if (
        !deleteEquipmentDialogState.equipmentId ||
        deleteEquipmentDialogState.isDeleting
      ) {
        return;
      }

      try {
        setDeleteEquipmentDialogState((state) => ({ ...state, isDeleting: true }));
        await deleteEquipment({
          equipmentId: deleteEquipmentDialogState.equipmentId,
          companyId,
          managedCompanyId,
        });
        toasterStore.push(
          alert(t('administration.equipment.equipment_deleted'), AlertTypes.success),
        );
        void reloadEquipmentPage();
      } catch {
        // Error is already handled in the hook
      } finally {
        setDeleteEquipmentDialogState({
          isDeleting: false,
          isOpen: false,
          equipmentId: undefined,
        });
      }
    };

    const deleteRow = (id: string) => {
      setDeleteEquipmentDialogState({ isOpen: true, equipmentId: id });
    };

    const columns = useMemo<Array<DataTableColumn<Equipment>>>(
      () => [
        {
          field: 'name',
          headerName: t('form_fields.name'),
          valueGetter: (equipment) => equipment.name,
        },
        {
          field: 'typeName',
          headerName: t('form_fields.equipment_type'),
          valueGetter: (equipment) => equipment.typeName,
        },
        {
          field: 'companyName',
          headerName: t('form_fields.company'),
          valueGetter: (equipment) => equipment.companyName,
        },
        {
          field: 'licenseNumber',
          headerName: t('form_fields.license_number'),
          valueGetter: (equipment) => equipment.licenseNumber,
        },
        {
          field: 'externalId',
          headerName: t('form_fields.equipment_id'),
          valueGetter: (equipment) => equipment.externalId,
        },
        actionsColumn((equipment) => {
          const hasCompanyShare = !!equipment.companyShare;

          const options = [];

          if (hasCompanyShare && equipment.companyShare?.id) {
            // remove share option (for external equipment)
            options.push({
              title: t('actions.remove_share'),
              icon: <ToggleOffOutlined />,
              callBack: () => {
                handleDeleteCompanyShare(equipment.companyShare!.id);
              },
            });
          } else {
            // delete option (for vendor's own equipment)
            options.push({
              title: t('actions.delete'),
              icon: <Delete />,
              callBack: () => deleteRow(equipment.id),
            });
          }

          return (
            <>
              <IconButton
                aria-label={t('actions.edit')}
                size="small"
                onClick={() => handleEditEquipment(equipment.id)}
              >
                <Edit fontSize="small" />
              </IconButton>
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            </>
          );
        }),
      ],
      [companyId, managedCompanyId],
    );

    return (
      <Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('administration.managed_companies_v2.manage_equipment_for')}
            {managedCompany.legalName}
          </Typography>
          <SmallButton
            color="brandV2Yellow"
            startIcon={<Add />}
            onClick={handleOpenEquipmentDialog}
          >
            {t('actions.add_equipment')}
          </SmallButton>
        </Box>
        <DataTable
          id="vendor-equipment-grid"
          columns={columns}
          rows={equipment}
          loading={
            isFetchingEquipment ||
            isEquipmentUpdating ||
            Boolean(deleteEquipmentDialogState.isDeleting)
          }
          onSearchChange={setSearchQuery}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingEquipment}
              onNavigate={(link) => void goToEquipmentPage(link)}
              pagination={equipmentPagination}
            />
          }
        />
        {/* Internal Equipment Dialog */}
        <EquipmentDialog
          ref={equipmentFormRef}
          isOpen={isEquipmentDialogOpen}
          onClose={handleCloseEquipmentDialog}
          selectedEquipment={selectedEquipment}
          companyShares={companyShares}
          companyId={companyId}
          isExternalEquipment={false}
          isUpdating={isEquipmentUpdating}
          isUpdatingCompanyShares={isUpdatingCompanyShares}
          asyncErrors={asyncErrors}
          onSubmit={handleSubmitEquipment}
          dialogTitle={
            selectedEquipment
              ? t('administration.equipment.update_equipment')
              : t('administration.equipment.create_equipment')
          }
          managedCompanyId={managedCompanyId}
        />
        {/* External Equipment Dialog */}
        <EquipmentDialog
          ref={externalEquipmentFormRef}
          isOpen={isExternalEquipmentDialogOpen}
          onClose={handleCloseExternalEquipmentDialog}
          selectedEquipment={selectedEquipment}
          companyShares={companyShares}
          companyId={companyId}
          isExternalEquipment={true}
          isUpdating={isEquipmentUpdating}
          isUpdatingCompanyShares={isUpdatingCompanyShares}
          asyncErrors={asyncErrors}
          onSubmit={handleSubmitExternalEquipment}
          dialogTitle={
            selectedEquipment
              ? t('administration.equipment.update_equipment')
              : t('administration.equipment.create_equipment')
          }
          managedCompanyId={managedCompanyId}
        />
        <ConfirmationDialog
          isBusy={deleteEquipmentDialogState.isDeleting}
          isOpen={deleteEquipmentDialogState.isOpen}
          onClose={() => {
            setDeleteEquipmentDialogState({
              isDeleting: false,
              isOpen: false,
              equipmentId: undefined,
            });
          }}
          onConfirm={deleteEquipmentCallback}
          title={t('administration.equipment.delete_equipment_title')}
        >
          <Typography>{t('administration.equipment.delete_equipment_text')}</Typography>
        </ConfirmationDialog>
      </Box>
    );
  },
);
